import React, { type ReactNode } from 'react';
import PageInfo from '../../atoms/PageInfo';
import {
  TOOLTIP_ALIGN_START,
  TOOLTIP_SIZE_MEDIUM,
  type TooltipPlacement,
} from '../../atoms/Tooltip';
import { LeftSlot, RightSlot, Shell, Title } from './TopBar.styled';

export interface TopBarProps {
  /**
   * Page title shown on the left (e.g. "Processes"). Optional when
   * `leftSlot` is provided — pages that need richer left content (breadcrumb
   * + status pill, etc.) supply `leftSlot` instead.
   */
  title?: string;
  /**
   * Optional help copy. When provided, renders a small info-circle icon next
   * to the title that reveals this text in a tooltip on hover/focus. Omit to
   * hide the help affordance entirely.
   *
   * Ignored when `leftSlot` is provided — at that point the consumer composes
   * its own left content and is responsible for any help affordance.
   */
  helpText?: ReactNode;
  /** Override the help button's aria-label (defaults to `About ${title}`). */
  helpAriaLabel?: string;
  /** Where the help tooltip opens; below the icon unless told otherwise. */
  helpPlacement?: TooltipPlacement;
  /**
   * Replace the default `title + helpText` left content with arbitrary nodes.
   * Use this for pages that need a breadcrumb, a status pill, or any left
   * composition that doesn't fit a single `h1` string. The TopBar still owns
   * the surrounding flex layout, padding, and divider — the consumer just
   * provides the inner content.
   *
   * When `leftSlot` is set, both `title` and `helpText` are ignored.
   */
  leftSlot?: ReactNode;
  /** Rendered before the left content, e.g. a mobile menu button. */
  leading?: ReactNode;
  /**
   * Right-side action slot — typically icon buttons (refresh, theme toggle)
   * and a `UserProfile`. The TopBar provides consistent spacing between
   * actions; consumers just drop nodes in.
   */
  actions?: ReactNode;
  bordered?: boolean;
  dataTestId?: string;
}

/**
 * Page chrome shell that sits at the top of every workspace page.
 *
 * Composition contract:
 *   - Left: the page title, with an optional info-circle icon that surfaces
 *     help copy in a tooltip on hover / focus.
 *   - Right: a free-form `actions` slot — drop in `IconButton`s for theme
 *     toggle, feedback, etc., and a `UserProfile` for the avatar menu.
 *
 * Purely presentational — owns layout, spacing, and the divider only. The
 * consumer remains in charge of every action's behaviour.
 */
const TopBar: React.FC<TopBarProps> = ({
  title,
  helpText,
  helpAriaLabel,
  helpPlacement,
  leftSlot,
  leading,
  actions,
  bordered = true,
  dataTestId = 'top-bar',
}) => (
  <Shell $bordered={bordered} data-testid={dataTestId}>
    {leading}
    <LeftSlot data-testid={`${dataTestId}__left`}>
      {leftSlot ?? (
        <>
          {title && <Title data-testid={`${dataTestId}__title`}>{title}</Title>}
          {helpText && (
            <PageInfo
              text={helpText}
              placement={helpPlacement}
              align={TOOLTIP_ALIGN_START}
              size={TOOLTIP_SIZE_MEDIUM}
              portal
              ariaLabel={helpAriaLabel ?? (title ? `About ${title}` : 'Help')}
              dataTestId={`${dataTestId}__help`}
            />
          )}
        </>
      )}
    </LeftSlot>
    {actions && (
      <RightSlot data-testid={`${dataTestId}__actions`}>{actions}</RightSlot>
    )}
  </Shell>
);

export default TopBar;
